/**
 * Incident Editor Dialog - Classification Tab (PROJ-007)
 * Categorización completa: industry, category, subcategory, type,
 * priority, impact, urgency, severity, origin, state
 */

import { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tag, Loader2 } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import type { IncidentFormData } from './types';
import { getActiveCategories, getSubcategories } from '@/lib/categoriesApi';
import type { Category, Subcategory } from '@/lib/categoriesApi';

/**
 * Calculate priority based on Impact + Urgency (ServiceNow pattern)
 * Priority matrix lookup table
 */
const PRIORITY_MATRIX: Record<string, Record<string, string>> = {
  high: { high: 'urgent', medium: 'high', low: 'high' },
  medium: { high: 'high', medium: 'medium', low: 'medium' },
  low: { high: 'high', medium: 'medium', low: 'low' },
};

function calculatePriority(impact?: string, urgency?: string): string {
  if (!impact || !urgency) return 'medium';
  return PRIORITY_MATRIX[impact]?.[urgency] ?? 'medium';
}

interface ClassificationTabProps {
  form: UseFormReturn<IncidentFormData>;
  isViewMode?: boolean;
}

export function ClassificationTab({ form, isViewMode = false }: ClassificationTabProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);

  // Load active categories from PostgreSQL
  useEffect(() => {
    async function fetchCategories() {
      try {
        setLoadingCategories(true);
        const data = await getActiveCategories();
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories([]); // Ensure categories is always an array
      } finally {
        setLoadingCategories(false);
      }
    }
    fetchCategories();
  }, []);

  // Watch category changes to load subcategories (CASCADE FILTERING)
  const selectedCategoryId = form.watch('categoryId');

  useEffect(() => {
    if (!selectedCategoryId) {
      setSubcategories([]);
      form.setValue('subcategoryId', '', { shouldDirty: false });
      return;
    }

    async function fetchSubcategories() {
      try {
        setLoadingSubcategories(true);
        const data = await getSubcategories(selectedCategoryId);
        setSubcategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error loading subcategories:', error);
        setSubcategories([]);
      } finally {
        setLoadingSubcategories(false);
      }
    }

    fetchSubcategories();
  }, [selectedCategoryId, form]);

  // Watch impact & urgency to auto-calculate priority
  const impact = form.watch('impact');
  const urgency = form.watch('urgency');

  // Update priority when impact or urgency changes
  const handleImpactUrgencyChange = () => {
    const calculatedPriority = calculatePriority(impact, urgency);
    form.setValue('priority', calculatedPriority, { shouldDirty: true });
  };

  return (
    <div className="space-y-6">
      <Alert>
        <Tag className="h-4 w-4" />
        <AlertDescription>
          Clasificación y categorización del incidente
        </AlertDescription>
      </Alert>

      {/* Category & Subcategory - Dynamic from PostgreSQL with CASCADE FILTERING */}
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="categoryId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Categoría</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value ?? ''}
                disabled={loadingCategories || isViewMode}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={loadingCategories ? "Cargando categorías..." : "Seleccionar categoría"} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="z-[9999]">
                  {loadingCategories ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Cargando...</span>
                    </div>
                  ) : categories.length === 0 ? (
                    <div className="py-4 text-center text-sm text-muted-foreground">
                      No hay categorías disponibles
                    </div>
                  ) : (
                    categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                        {category.description && (
                          <span className="text-xs text-muted-foreground block mt-0.5">
                            {category.description}
                          </span>
                        )}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <FormDescription>
                Clasificación principal del incidente
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="subcategoryId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Subcategoría</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value ?? ''}
                disabled={!selectedCategoryId || loadingSubcategories || isViewMode}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        !selectedCategoryId
                          ? "Primero seleccione una categoría"
                          : loadingSubcategories
                          ? "Cargando subcategorías..."
                          : "Seleccionar subcategoría"
                      }
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="z-[9999]">
                  {loadingSubcategories ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Cargando...</span>
                    </div>
                  ) : !selectedCategoryId ? (
                    <div className="py-4 text-center text-sm text-muted-foreground">
                      Seleccione primero una categoría
                    </div>
                  ) : subcategories.length === 0 ? (
                    <div className="py-4 text-center text-sm text-muted-foreground">
                      No hay subcategorías disponibles
                    </div>
                  ) : (
                    subcategories.map((subcategory) => (
                      <SelectItem key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                        {subcategory.description && (
                          <span className="text-xs text-muted-foreground block mt-0.5">
                            {subcategory.description}
                          </span>
                        )}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <FormDescription>
                Filtrada por categoría seleccionada (Cascade Filtering)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Type */}
      <FormField
        control={form.control}
        name="type"
        render={({ field }) => (
          <FormItem>
              <FormLabel>Tipo de Incidente *</FormLabel>
            <Select onValueChange={field.onChange} value={field.value ?? ''} disabled={isViewMode}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="z-[var(--z-popover-in-dragging-dialog)]">
                <SelectItem value="data_breach">Brecha de Datos</SelectItem>
                <SelectItem value="unauthorized_access">Acceso No Autorizado</SelectItem>
                <SelectItem value="data_loss">Pérdida de Datos</SelectItem>
                <SelectItem value="phishing">Phishing</SelectItem>
                <SelectItem value="malware">Malware</SelectItem>
                <SelectItem value="policy_violation">Violación de Política</SelectItem>
                <SelectItem value="consent_withdrawal">Retiro de Consentimiento</SelectItem>
                <SelectItem value="subject_rights_request">Solicitud de Derechos ARCO</SelectItem>
                <SelectItem value="system_outage">Interrupción de Sistema</SelectItem>
                <SelectItem value="performance_issue">Problema de Rendimiento</SelectItem>
                <SelectItem value="configuration_error">Error de Configuración</SelectItem>
                <SelectItem value="other">Otro</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Impact & Urgency (ServiceNow pattern) */}
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="impact"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Impacto</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  handleImpactUrgencyChange();
                }}
                value={field.value ?? ''}
                disabled={isViewMode}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Impacto de negocio" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="z-[9999]">
                  <SelectItem value="high">Alto - Afecta múltiples usuarios/sistemas</SelectItem>
                  <SelectItem value="medium">Medio - Afecta algunos usuarios</SelectItem>
                  <SelectItem value="low">Bajo - Afecta un usuario</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                Impacto al negocio
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="urgency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Urgencia</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  handleImpactUrgencyChange();
                }}
                value={field.value ?? ''}
                disabled={isViewMode}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Sensibilidad al tiempo" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="z-[9999]">
                  <SelectItem value="high">Alta - Requiere atención inmediata</SelectItem>
                  <SelectItem value="medium">Media - Puede esperar algunas horas</SelectItem>
                  <SelectItem value="low">Baja - Puede esperar días</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                Sensibilidad al tiempo
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Priority (auto-calculated or manual) */}
      <FormField
        control={form.control}
        name="priority"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Prioridad *</FormLabel>
            <Select onValueChange={field.onChange} value={field.value ?? ''} disabled={isViewMode}>
              <FormControl>
                <SelectTrigger className="font-semibold">
                  <SelectValue placeholder="Prioridad" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="z-[var(--z-popover-in-dragging-dialog)]">
                <SelectItem value="urgent" className="font-semibold text-red-600">
                  🔴 Urgente
                </SelectItem>
                <SelectItem value="high" className="font-semibold text-orange-600">
                  🟠 Alta
                </SelectItem>
                <SelectItem value="medium" className="font-semibold text-yellow-600">
                  🟡 Media
                </SelectItem>
                <SelectItem value="low" className="font-semibold text-green-600">
                  🟢 Baja
                </SelectItem>
              </SelectContent>
            </Select>
            <FormDescription>
              {impact && urgency ? 'Auto-calculada desde Impacto + Urgencia' : 'Establezca Impacto y Urgencia para auto-calcular'}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Severity */}
      <FormField
        control={form.control}
        name="severity"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Severidad Técnica *</FormLabel>
            <Select onValueChange={field.onChange} value={field.value ?? ''} disabled={isViewMode}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Severidad técnica" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="z-[var(--z-popover-in-dragging-dialog)]">
                <SelectItem value="critical">Crítica - Sistema completamente inoperante</SelectItem>
                <SelectItem value="high">Alta - Funcionalidad principal afectada</SelectItem>
                <SelectItem value="medium">Media - Funcionalidad secundaria afectada</SelectItem>
                <SelectItem value="low">Baja - Impacto mínimo</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription>
              Severidad técnica (distinto a prioridad de negocio)
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Origin */}
      <FormField
        control={form.control}
        name="origin"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Origen</FormLabel>
            <Select onValueChange={field.onChange} value={field.value ?? ''} disabled={isViewMode}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="¿Cómo se reportó?" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="z-[var(--z-popover-in-dragging-dialog)]">
                <SelectItem value="web">Web - Portal de usuario</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="phone">Teléfono</SelectItem>
                <SelectItem value="chat">Chat</SelectItem>
                <SelectItem value="api">API - Integración externa</SelectItem>
                <SelectItem value="manual">Manual - Creación interna</SelectItem>
                <SelectItem value="imported">Importado</SelectItem>
                <SelectItem value="monitoring">Monitoreo Automático</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription>
              Canal por el cual se reportó el incidente
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* State */}
      <FormField
        control={form.control}
        name="state"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Estado *</FormLabel>
            <Select onValueChange={field.onChange} value={field.value ?? ''} disabled={isViewMode}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Estado actual" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="z-[var(--z-popover-in-dragging-dialog)]">
                <SelectItem value="new">Nuevo</SelectItem>
                <SelectItem value="assigned">Asignado</SelectItem>
                <SelectItem value="investigation">En Investigación</SelectItem>
                <SelectItem value="containment">En Contención</SelectItem>
                <SelectItem value="remediation">En Remediación</SelectItem>
                <SelectItem value="post_incident">Post-Incidente</SelectItem>
                <SelectItem value="resolved">Resuelto</SelectItem>
                <SelectItem value="closed">Cerrado</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
